import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";
import { getSafeErrorMessage } from "@/lib/utils/safe-error";
import { getSupabase, withTimeout } from "@/lib/hooks/api/utils";

const createTicketTemplateSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  category: z.string().nullable().optional(),
  title_template: z.string().min(1),
  description_template: z.string().min(1),
  estimated_hours: z.preprocess(
    (val) => (val === "" || val === undefined ? undefined : Number(val)),
    z.number().positive().optional()
  ),
  sla_hours: z.number().positive().optional(),
  tags: z.array(z.string()).optional(),
  is_active: z.boolean().optional(),
});

const updateTicketTemplateSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).optional(),
  description: z.string().optional(),
  category: z.string().nullable().optional(),
  title_template: z.string().min(1).optional(),
  description_template: z.string().min(1).optional(),
  estimated_hours: z.preprocess(
    (val) => (val === "" || val === undefined ? undefined : Number(val)),
    z.number().positive().optional()
  ),
  sla_hours: z.number().positive().optional(),
  tags: z.array(z.string()).optional(),
  is_active: z.boolean().optional(),
});

export type CreateTicketTemplateInput = z.input<typeof createTicketTemplateSchema>;
export type UpdateTicketTemplateInput = z.input<typeof updateTicketTemplateSchema>;

export function useCreateTicketTemplate() {
  const queryClient = useQueryClient();
  const schema = createTicketTemplateSchema;

  return useMutation({
    mutationFn: async (payload: CreateTicketTemplateInput) => {
      const supabase = getSupabase();
      let validated: z.infer<typeof schema>;
      try {
        validated = schema.parse(payload);
      } catch (err) {
        throw new Error(getSafeErrorMessage(err));
      }

      const { data: authData, error: authError } = await supabase.auth.getUser();
      if (authError || !authData?.user) throw new Error("User not authenticated");

      const { data, error } = await withTimeout(
        supabase
          .from("ticket_templates")
          .insert([{ ...validated, created_by: authData.user.id }])
          .select()
          .single(),
        15000,
        "create ticket template"
      );
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ticket-templates"] });
      toast.success("Template created");
    },
    onError: (err) => {
      toast.error(getSafeErrorMessage(err));
      console.error(err);
    },
  });
}

export function useUpdateTicketTemplate() {
  const queryClient = useQueryClient();
  const schema = updateTicketTemplateSchema;

  return useMutation({
    mutationFn: async (payload: UpdateTicketTemplateInput) => {
      const supabase = getSupabase();
      let validated: z.infer<typeof schema>;
      try {
        validated = schema.parse(payload);
      } catch (err) {
        throw new Error(getSafeErrorMessage(err));
      }

      const { id, ...updates } = validated;
      const { data, error } = await withTimeout(
        supabase.from("ticket_templates").update(updates).eq("id", id).select().single(),
        15000,
        "update ticket template"
      );
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ticket-templates"] });
      toast.success("Template updated");
    },
    onError: (err) => {
      toast.error(getSafeErrorMessage(err));
      console.error(err);
    },
  });
}

export function useDeleteTicketTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const supabase = getSupabase();
      const { error } = await withTimeout(
        supabase.from("ticket_templates").delete().eq("id", id),
        15000,
        "delete ticket template"
      );
      if (error) throw new Error(error.message);
      return true;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ticket-templates"] });
      toast.success("Template deleted");
    },
    onError: (err) => {
      toast.error("Failed to delete template");
      console.error(err);
    },
  });
}

export function useToggleTicketTemplateActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const supabase = getSupabase();
      const { data, error } = await withTimeout(
        supabase.from("ticket_templates").update({ is_active }).eq("id", id).select().single(),
        15000,
        "toggle ticket template active"
      );
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ticket-templates"] });
    },
  });
}
